import React, { useCallback, useEffect, useMemo } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import Breadcrumbs from "../components/UI/Breadcrumbs/Breadcrumbs.tsx";
import BotoneraModern from "../components/Botonera/BotoneraModern.tsx";
import useNavVisibility from "../hooks/useNavVisibility.ts";
import {
  viewAccessRequest,
  setViewNotification,
  openMainModal,
  validateAccessRequest,
  deleteNotification,
} from "../store/actions";
import type { ApiNotification } from "./notificationEntityShape.ts";
import type { NotificationHubItem } from "./mapApiNotificationToHubItem.ts";
import { mapApiNotificationsList } from "./mapApiNotificationToHubItem.ts";
import NotificationHubList from "./NotificationHubList.tsx";
import hubPageStyles from "./NotificationHubPage.module.scss";
import solicitudesLayoutStyles from "../pages/Solicitudes/Solicitudes.module.scss";
import { POSTULATION_ACTION_TYPES } from "../constants/postulations.ts";
import { getPostulationPermisoId } from "./notificationPostulationHelpers.ts";
import { HUB_NOTIFICATION_TYPES_VISIBLE_ONLY_TO_ADMIN } from "./notificationTypes.ts";

const Container = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding-right: 2%;
  padding-left: 2%;
  max-width: 150rem;
  margin: auto;

  @media (max-width: 900px) {
    padding-right: 0;
    padding-left: 0;
  }

  @media (max-width: 600px) {
    padding-right: 0;
    padding-left: 0;
  }
`;

const OverflowContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 2rem 0;

  @media (max-width: 900px) {
    padding: 0;
    padding-top: 6rem;
  }

  @media (max-width: 600px) {
    padding: 0;
    padding-top: 6rem;
  }
`;

type MainModalPayload = {
  title: string;
  body: string;
  iconType?: string;
  isSuccess: boolean;
  confirmHandler: (() => void) | null;
};

type UserDataShape = {
  rols?: { name?: string }[];
};

export type NotificationHubPageProps = {
  solicitudesList: ApiNotification[] | null | undefined;
  userData: UserDataShape | undefined;
  viewAccessRequest: () => void;
  setViewNotification: (id: string | number) => void;
  openMainModal: (config: MainModalPayload) => void;
  validateAccessRequest: (
    permisoId: number,
    status: string,
    solicitudType: string
  ) => Promise<unknown> | void;
  deleteNotification: (id: number) => void;
};

const NotificationHubPage: React.FC<NotificationHubPageProps> = ({
  solicitudesList,
  userData,
  viewAccessRequest,
  setViewNotification,
  openMainModal,
  validateAccessRequest,
  deleteNotification,
}) => {
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();

  useEffect(() => {
    viewAccessRequest();
  }, [viewAccessRequest]);

  const isAdmin = Boolean(userData?.rols?.some((r) => r.name === "ADMIN"));

  const items = useMemo(() => {
    const list = Array.isArray(solicitudesList) ? solicitudesList : [];
    const mapped = mapApiNotificationsList(list as ApiNotification[]);
    if (isAdmin) return mapped;
    return mapped.filter(
      (i) => !HUB_NOTIFICATION_TYPES_VISIBLE_ONLY_TO_ADMIN.has(i.type)
    );
  }, [solicitudesList, isAdmin]);

  const handleMarkRead = (id: string) => {
    const n = Number(id);
    if (Number.isFinite(n)) {
      setViewNotification(n);
    } else {
      setViewNotification(id);
    }
  };

  const markReadIfNeeded = useCallback(
    (item: NotificationHubItem) => {
      if (item.read || !item.id) return;
      const nid = Number(item.id);
      if (Number.isFinite(nid)) setViewNotification(nid);
    },
    [setViewNotification]
  );

  const handlePostulationAccept = useCallback(
    (item: NotificationHubItem) => {
      const permisoId = getPostulationPermisoId(item);
      if (permisoId === null) return;
      const name = (item.actorLabel || "Usuario").trim();
      openMainModal({
        title: "¿Estás seguro que deseas aceptar la solicitud?",
        body: `Si aceptas el NDA el usuario ${name} podrá acceder a la documentación del proyecto y enviarte presupuestos para ayudar a completar el mismo.`,
        iconType: "warning",
        isSuccess: false,
        confirmHandler: () => {
          const result = validateAccessRequest(
            permisoId,
            "ALLOWED",
            POSTULATION_ACTION_TYPES.ACCEPTED
          );
          Promise.resolve(result)
            .then(() => markReadIfNeeded(item))
            .catch(() => {});
        },
      });
    },
    [markReadIfNeeded, openMainModal, validateAccessRequest]
  );

  const handleDeleteNotification = useCallback(
    (item: NotificationHubItem) => {
      const n = Number(item.id);
      if (!Number.isFinite(n)) return;
      deleteNotification(n);
    },
    [deleteNotification]
  );

  const handlePostulationReject = useCallback(
    (item: NotificationHubItem) => {
      const permisoId = getPostulationPermisoId(item);
      if (permisoId === null) return;
      const name = (item.actorLabel || "Usuario").trim();
      openMainModal({
        title: "¿Estás seguro que deseas rechazar la solicitud?",
        body: `Si rechazas el NDA no podrás recibir más propuestas de ${name} para este proyecto.`,
        iconType: "error",
        isSuccess: false,
        confirmHandler: () => {
          const result = validateAccessRequest(
            permisoId,
            "NEGATED",
            POSTULATION_ACTION_TYPES.NEGATED
          );
          Promise.resolve(result)
            .then(() => markReadIfNeeded(item))
            .catch(() => {});
        },
      });
    },
    [markReadIfNeeded, openMainModal, validateAccessRequest]
  );

  return (
    <Container>
      {ocultarEnMobile ? (
        <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />
      ) : null}
      <OverflowContainer>
        <div className={solicitudesLayoutStyles.breadcrumbsContainer}>
          <Breadcrumbs />
        </div>
        <div className={hubPageStyles.shell}>
          <NotificationHubList
            items={items}
            onMarkRead={handleMarkRead}
            onPostulationAccept={handlePostulationAccept}
            onPostulationReject={handlePostulationReject}
            onDelete={handleDeleteNotification}
          />
        </div>
      </OverflowContainer>
    </Container>
  );
};

const mapStateToProps = (state: {
  dashboard?: { solicitudesList?: ApiNotification[] };
  general?: { userData?: UserDataShape };
}) => ({
  solicitudesList: state.dashboard?.solicitudesList,
  userData: state.general?.userData,
});

export default connect(mapStateToProps, {
  viewAccessRequest,
  setViewNotification,
  openMainModal,
  validateAccessRequest,
  deleteNotification,
})(NotificationHubPage);
